/**
 * 环境配置中心
 * 统一管理所有环境的配置，避免重复代码
 * 
 * 使用方式：
 * 1. 在 config.[env].ts 中导入对应环境的配置
 * 2. 在 proxy.ts 中使用 envConfigs[env] 获取代理配置
 * 3. 在代码中通过 define 注入的全局变量使用（如 API_SERVER, LOGIN_SERVER_BASE_URL 等）
 */

export type EnvType = 'dev' | 'test' | 'pre' | 'prod';

export interface EnvConfig {
  // Titan 服务（主业务 API）
  apiServer: string;
  
  // 登录服务
  loginServerBaseUrl: string;
  
  // ITSM 服务
  itsmServerBaseUrl: string;
  
  // WebSocket 服务
  wsUrl: string;
  
  // Titan Server（前端页面）
  titanServer: string;
  
  // Minder Server（前端页面）
  minderServer: string;
  
  // 代理配置（仅开发环境使用）
  proxy?: {
    serverBaseUrl: string;
    loginServerBaseUrl: string;
    itsmServerBaseUrl: string;
  };
}

/**
 * 所有环境的配置数据
 */
export const envConfigs: Record<EnvType, EnvConfig> = {
  dev: {
    apiServer: 'http://localhost:8000',
    loginServerBaseUrl: 'https://dev-devops.52imile.cn/devops-login',
    itsmServerBaseUrl: 'http://localhost:5555',
    wsUrl: 'ws://127.0.0.1:8080/websocket/',
    titanServer: 'https://dev-devops.52imile.cn/release#/releaseBoard',
    minderServer: 'https://dev-devops.52imile.cn/release/#/minder',
    proxy: {
      serverBaseUrl: 'http://localhost:8080',
      loginServerBaseUrl: 'https://dev-devops.52imile.cn/devops-login',
      itsmServerBaseUrl: 'http://localhost:5555/itsm',
    },
  },
  test: {
    apiServer: 'https://dev-devops.52imile.cn',
    loginServerBaseUrl: 'https://dev-devops.52imile.cn/devops-login',
    itsmServerBaseUrl: 'https://dev-devops.52imile.cn/itsm',
    wsUrl: 'wss://dev-devops.52imile.cn/prod-api/websocket/',
    titanServer: 'https://dev-devops.52imile.cn/release#/releaseBoard',
    minderServer: 'https://dev-devops.52imile.cn/release/#/minder',
    proxy: {
      serverBaseUrl: 'https://dev-devops.52imile.cn/titan',
      loginServerBaseUrl: 'https://dev-devops.52imile.cn/devops-login',
      itsmServerBaseUrl: 'https://dev-devops.52imile.cn/itsm',
    },
  },
  pre: {
    apiServer: 'https://pre-devops.imile-inc.com',
    loginServerBaseUrl: 'https://pre-devops.imile-inc.com/devops-login',
    itsmServerBaseUrl: 'https://pre-devops.imile-inc.com/itsm',
    wsUrl: 'wss://pre-devops.imile-inc.com/prod-api/websocket/',
    titanServer: 'https://pre-devops.imile-inc.com/release#/releaseBoard',
    minderServer: 'https://pre-devops.imile-inc.com/release#/minder',
    proxy: {
      serverBaseUrl: 'https://pre-devops.imile-inc.com/titan',
      loginServerBaseUrl: 'https://pre-devops.imile-inc.com/devops-login',
      itsmServerBaseUrl: 'https://pre-devops.imile-inc.com/itsm',
    },
  },
  prod: {
    apiServer: 'https://devops.imile-inc.com',
    loginServerBaseUrl: 'https://devops.imile-inc.com/devops-login',
    itsmServerBaseUrl: 'https://devops.imile-inc.com/itsm',
    wsUrl: 'wss://devops.imile-inc.com/prod-api/websocket/',
    titanServer: 'https://devops.imile-inc.com/release#/releaseBoard',
    minderServer: 'https://devops.imile-inc.com/release#/minder',
    // 生产环境不需要 proxy 配置
  },
};

/**
 * 获取当前环境的配置
 * @param env 环境类型，默认为 'dev'
 */
export function getEnvConfig(env?: EnvType): EnvConfig {
  const envType = (env || process.env.REACT_APP_ENV || 'dev') as EnvType;
  return envConfigs[envType] || envConfigs.dev;
}

